import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCcw, Sparkles } from 'lucide-react';
import { useGenerateFraming, useUpdateProject } from '@/hooks/use-projects';
import { toast } from '@/hooks/use-toast';
import { SectionLabel } from '@/components/ui/section-label';
import { openerGreeting } from '@/lib/opener-template';
import { deriveFramingState, isFramingCurrent } from './framing-state';
import type { Project } from '@/types/project';

interface FramingCardProps {
  project: Project;
  onOpenCoach: () => void;
}

// Step-4 "What respondents will hear" card: the PM-reviewed, respondent-safe
// framing derived from the aspiration. Doubles as the aspiration-quality
// lint — a method-shaped aspiration can't produce a framing, and that
// failure surfaces here (soft gate: warns, never blocks anything).
export function FramingCard({ project, onOpenCoach }: FramingCardProps) {
  const generate = useGenerateFraming(project.id);
  const update = useUpdateProject();
  const [framing, setFraming] = useState(project.respondentFraming ?? '');
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(project.openerBody ?? '');

  // Re-sync drafts when the server values change (after generate/save).
  useEffect(() => {
    setFraming(project.respondentFraming ?? '');
    setBody(project.openerBody ?? '');
  }, [project.respondentFraming, project.openerBody]);

  // Auto-draft when the current aspiration has no framing and the PM hasn't
  // hand-edited one (a hand edit gets the stale banner instead, never an
  // overwrite). One attempt per aspiration value per mount, so a failure
  // shows the warning with a manual retry instead of looping.
  const attemptedFor = useRef<string | null>(null);
  const aspiration = project.aspiration?.trim() ?? '';
  useEffect(() => {
    if (!aspiration || isFramingCurrent(project) || project.framingEdited || generate.isPending) return;
    if (attemptedFor.current === aspiration) return;
    attemptedFor.current = aspiration;
    generate.mutate();
    // generate is a stable mutation handle; project identity churns per fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspiration, project.respondentFraming, project.openerBody, project.framingSourceAspiration, project.framingEdited]);

  const state = deriveFramingState({ ...project, generating: generate.isPending });
  if (state === 'hidden') return null;

  const dirty = framing !== (project.respondentFraming ?? '') || body !== (project.openerBody ?? '');
  const failureReason =
    generate.data && !generate.data.framing
      ? generate.data.reason
      : generate.isError
        ? 'generation_failed'
        : undefined;

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        projectId: project.id,
        patch: { respondentFraming: framing.trim() || null, openerBody: body.trim() || null },
      });
      // A hand-save supersedes any earlier failed draft attempt; clear the
      // mutation state so its failure note doesn't linger under saved text.
      generate.reset();
      return true;
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save.',
        variant: 'destructive',
      });
      return false;
    }
  };

  return (
    <div className="space-y-2 rounded-lg border bg-card p-4 text-sm">
      <SectionLabel>How your customer conversations open</SectionLabel>
      <p className="max-w-prose text-xs text-muted-foreground">
        Respondents see this opener when they join a conversation. It comes from your
        aspiration and doesn&apos;t mention your product.
      </p>
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer underline-offset-2 hover:underline">
          Why not mention my product?
        </summary>
        <p className="mt-1 max-w-prose">
          Naming your idea nudges people into telling you what you want to hear. [role] and
          [company] come from each customer&apos;s details; naming the organisation can be
          switched off per dialog below.
        </p>
      </details>

      {state === 'generating' && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Drafting what respondents will hear...
        </p>
      )}

      {state === 'missing' && (
        <div className="space-y-2">
          <p className={failureReason ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}>
            {failureReason === 'no_concrete_subject'
              ? 'This aspiration reads as your research method, not a customer outcome, so customers would see a generic opening. Refine it with the coach in step 1.'
              : failureReason === 'generation_failed'
                ? "Couldn't draft an opening. Customers will see a generic one until this is fixed."
                : 'No opening drafted yet, so customers would see a generic one.'}
          </p>
          <div className="flex items-center gap-2">
            {failureReason === 'no_concrete_subject' && (
              <Button size="sm" variant="outline" onClick={onOpenCoach}>
                <Sparkles className="h-3 w-3 mr-1" />
                Refine in step 1
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => generate.mutate()} disabled={generate.isPending}>
              <RefreshCcw className="h-3 w-3 mr-1" />
              {failureReason ? 'Try again' : 'Draft it'}
            </Button>
          </div>
        </div>
      )}

      {(state === 'ok' || state === 'stale') && (
        <div className="space-y-2">
          {state === 'stale' && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Your aspiration changed since this was written. Regenerate to match it.
            </p>
          )}
          {!editing ? (
            <>
              {/* The opener IS the content — show it at reading size; the form
                  only appears on Edit. */}
              {project.respondentFraming && (
                <p className="text-sm text-muted-foreground">Focused on: {project.respondentFraming}</p>
              )}
              <p className="max-w-prose text-sm italic text-muted-foreground">
                {openerGreeting(true)}
                {(project.openerBody ?? '').trim() || '...'}
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => generate.mutate()} disabled={generate.isPending}>
                  <RefreshCcw className="h-3 w-3 mr-1" />
                  Regenerate
                </Button>
              </div>
            </>
          ) : (
            <>
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">
              Header focus (shown as &quot;Focused on: ...&quot;)
            </span>
            <input
              value={framing}
              onChange={(e) => setFraming(e.target.value.slice(0, 500))}
              className="w-full px-2 py-1 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-1 focus:ring-primary"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-muted-foreground">Opening message</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 2000))}
              rows={3}
              className="w-full px-2 py-1 border rounded-md bg-background text-sm resize-none focus:outline-hidden focus:ring-1 focus:ring-primary"
            />
          </label>
          <p className="text-xs text-muted-foreground">
            They&apos;ll see:{' '}
            <span className="italic">
              {openerGreeting(true)}
              {body.trim() || '...'}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                void handleSave().then((ok) => {
                  if (ok) setEditing(false);
                });
              }}
              disabled={!dirty || update.isPending}
            >
              {update.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setFraming(project.respondentFraming ?? '');
                setBody(project.openerBody ?? '');
                setEditing(false);
              }}
              disabled={update.isPending}
            >
              Cancel
            </Button>
          </div>
            </>
          )}
          {failureReason && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {failureReason === 'no_concrete_subject'
                ? "A fresh draft wasn't possible: the aspiration reads as your research method, not a customer outcome. Your saved text is unchanged."
                : "Couldn't regenerate. Your saved text is unchanged. Try again in a moment."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
